import { Button, Loader } from '@aws-amplify/ui-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { AiFillWarning } from 'react-icons/ai';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { TbCheck, TbCircle, TbCopy, TbDatabase, TbDownload, TbFileTypeCsv, TbJson } from 'react-icons/tb';
import { TokenPagination } from '../../components/TokenPagination';
import Banner from '../../custom-components/Banner';
import ExportResultsTable from '../../custom-components/ExportResultsTable';
import LoadingBars from '../../custom-components/LoadingBars';
import '../../custom-components/ReviewProgressSteps.scss';
import '../ExportDetail.scss';
import { useCopyExport } from '../../hooks/useCopyExport';
import { allPages } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { getHubspotUrl } from '../../utils/CrmUtils';
import General from '../../utils/GeneralUtils';
import { ExecuteStep, ExportContext } from '../ExportDetail';
import { RECORD_STATUSES, ReviewRow, reviewRowOf } from './exportShapes';

const EXECUTE_STEPS: { key: ExecuteStep; label: string }[] = [
  { key: 'PREPARING', label: 'Preparing export' },
  { key: 'EXPORTING', label: 'Exporting records' },
  { key: 'FINALIZING', label: 'Finalizing' },
];

export function ExportStepExport() {
  const exportContext = useContext(ExportContext);
  const { copyExport, isCopying } = useCopyExport();
  const [currentStep, setCurrentStep] = useState<ExecuteStep>('PREPARING');
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportedRecords, setExportedRecords] = useState<ReviewRow[] | null>(null);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  // pageTokens[i] loads page i + 1; the last is the page shown
  const [pageTokens, setPageTokens] = useState<string[]>(['']);
  const [nextPageToken, setNextPageToken] = useState('');
  const pageRequest = useRef(0);
  const [statusFilter, setStatusFilter] = useState<'SUCCESS' | 'FAILED' | 'SKIPPED' | null>(null);
  const [uniqueErrors, setUniqueErrors] = useState<Array<{ error: string; count: number }>>([]);
  const pageSize = 50;

  const exportId = exportContext?.exportId;
  const team = { team: exportContext?.teamId };
  const exportStatus = exportContext?.exportData?.status;
  const exportTarget = exportContext?.exportData?.target;

  // Determine state based on export status
  const isInProgress = exportStatus === 'EXPORT_PROGRESS' || exportStatus === 'IN_PROGRESS';
  const isCompleted = exportStatus === 'COMPLETED' || exportStatus === 'SUCCESS' || exportStatus === 'PARTIAL_SUCCESS';
  const isPartialSuccess = exportStatus === 'PARTIAL_SUCCESS';
  const isFailed = exportStatus === 'FAILURE' || exportStatus === 'REJECTED';
  const isCrmExport = exportTarget === 'HUBSPOT' || exportTarget === 'SALESFORCE';

  // Log mount and status for debugging race conditions
  useEffect(() => {
    console.log('[ExportStepExport] Component mounted', {
      exportId,
      exportStatus,
      isInProgress,
      isCompleted,
      isFailed,
      hasContext: !!exportContext,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Log status changes
  useEffect(() => {
    console.log('[ExportStepExport] Status changed', {
      exportId,
      exportStatus,
      isInProgress,
      isCompleted,
      isFailed,
      currentStep,
      timestamp: new Date().toISOString()
    });
  }, [exportStatus, isInProgress, isCompleted, isFailed]);

  // Reset saving state on mount
  useEffect(() => {
    exportContext?.setIsSaving(false);
  }, []);

  // Fetch unique errors when export completes or fails (CRM exports only)
  useEffect(() => {
    if (!exportId || (!isCompleted && !isFailed) || !isCrmExport) return;

    const fetchErrors = async () => {
      try {
        const errors = await allPages(eduleadV4.exports.listReviewErrors({ export_id: exportId, page_size: 500 }, team));
        setUniqueErrors(errors.map(e => ({ error: e.message ?? '', count: e.count ?? 0 })));
      } catch (err) {
        console.error('Error fetching review errors:', err);
      }
    };

    fetchErrors();
  }, [exportId, isCompleted, isFailed, isCrmExport]);

  // Loads the exported records' page whose token is last in `tokens`; a response to a superseded
  // request (another filter or page) is dropped.
  const loadPage = async (tokens: string[]) => {
    if (!exportId) return;
    const request = ++pageRequest.current;
    setIsLoadingRecords(true);
    try {
      const page = await eduleadV4.exports.listReviewRecords(
        {
          export_id: exportId,
          'filter.decision': 'EXPORT_DECISION_TAKE',
          'filter.export_status': statusFilter ? RECORD_STATUSES[statusFilter] : undefined,
          page_size: pageSize,
          page_token: tokens[tokens.length - 1] || undefined
        },
        team
      );
      if (request !== pageRequest.current) return;
      setPageTokens(tokens);
      setExportedRecords(page.data.map(reviewRowOf));
      setNextPageToken(page.response.next_page_token ?? '');
    } catch (err) {
      if (request !== pageRequest.current) return;
      console.error('Error fetching exported records:', err);
    } finally {
      if (request === pageRequest.current) setIsLoadingRecords(false);
    }
  };

  // Fetch exported records when export completes or fails (CRM exports only), from the first page
  // when the filter changes
  useEffect(() => {
    if (!exportId || (!isCompleted && !isFailed) || !isCrmExport) return;
    loadPage(['']);
  }, [exportId, isCompleted, isFailed, isCrmExport, pageSize, statusFilter]);

  // Handle stat box click - toggle filter
  const handleStatClick = (status: 'SUCCESS' | 'FAILED' | 'SKIPPED') => {
    setStatusFilter(prev => prev === status ? null : status);
  };

  // Use execute step from parent context (Pusher subscription is managed there)
  // This avoids race conditions because ExportDetail subscribes earlier
  useEffect(() => {
    if (exportContext?.currentExecuteStep) {
      console.log('[ExportStepExport] Received step from context', { step: exportContext.currentExecuteStep });
      setCurrentStep(exportContext.currentExecuteStep);
    }
  }, [exportContext?.currentExecuteStep]);

  // Download the exported file
  const handleDownload = async () => {
    if (!exportId) return;

    setIsDownloading(true);
    try {
      // GetExport signs a fresh link for a successful file export
      const { export: current } = await eduleadV4.exports.get({ id: exportId }, team);
      if (current?.download_uri) {
        await General.downloadUrl(current.download_uri, current.file_name ?? '');
      }
    } catch (err) {
      console.error('Error downloading export:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyToNew = () => {
    if (exportId) copyExport(exportId);
  };

  const readOnly = exportContext?.readOnly ?? false;

  // Don't render copy section in read-only mode (boss admin view)
  const renderCopySection = () => {
    if (readOnly) return null;
    return (
      <div className="export-success__copy-section">
        <Button
          variation="primary"
          size="small"
          className="link"
          onClick={handleCopyToNew}
          isLoading={isCopying}
          loadingText="Copying..."
        >
          <TbCopy size={16} style={{ marginRight: 6 }} />
          Copy to New Export
        </Button>
      </div>
    );
  };

  if (!exportContext) {
    return (
      <div className="content content-1200">
        <div className="export-step">
          <LoadingBars numberOfRows={5} />
        </div>
      </div>
    );
  }

  // Get export counts from DB columns
  const exportData = exportContext?.exportData;
  const successCount = exportData?.success_count ?? 0;
  const failedCount = exportData?.failed_count ?? 0;
  const skippedCount = exportData?.skipped_count ?? 0;
  // Always compute recordCount as sum of individual counts for consistency
  const recordCount = successCount + failedCount + skippedCount;

  // Render results table with pagination (shared between success and failure)
  const renderResultsTable = () => {
    if (isLoadingRecords || !exportedRecords) {
      return <LoadingBars numberOfRows={5} />;
    }

    const salesforceObject = exportContext?.exportData?.options?.salesforce_object;

    return (
      <>
        <ExportResultsTable
          records={exportedRecords}
          crmType={exportTarget as 'HUBSPOT' | 'SALESFORCE'}
          hubspotPortalId={exportContext?.hubspotPortalId || exportData?.hubspot_portal_id || undefined}
          salesforceInstanceUrl={exportContext?.salesforceInstanceUrl}
          entityType={exportContext?.exportData?.entity as 'CONTACT' | 'INSTITUTION'}
          salesforceObject={salesforceObject === 'EXPORT_SALESFORCE_OBJECT_CONTACT' ? 'Contact' : salesforceObject === 'EXPORT_SALESFORCE_OBJECT_LEAD' ? 'Lead' : undefined}
          isLoading={isLoadingRecords}
        />

        {(pageTokens.length > 1 || nextPageToken) && (
          <div className="export-success__pagination">
            <TokenPagination
              page={pageTokens.length}
              pageSize={pageSize}
              shown={exportedRecords.length}
              hasNext={!!nextPageToken}
              isLoading={isLoadingRecords}
              onPrevious={() => loadPage(pageTokens.slice(0, -1))}
              onNext={() => loadPage([...pageTokens, nextPageToken])}
              inline
            />
          </div>
        )}
      </>
    );
  };

  // Render progress steps (same pattern as ReviewProgressSteps)
  const renderProgressSteps = () => {
    const currentIndex = EXECUTE_STEPS.findIndex(s => s.key === currentStep);

    return (
      <div className="review-progress-steps">
        {EXECUTE_STEPS.map((step, index) => {
          const status = index < currentIndex ? 'completed'
                       : index === currentIndex ? 'in_progress'
                       : 'pending';
          return (
            <div key={step.key} className={`review-progress-step review-progress-step--${status}`}>
              <div className="review-progress-step__icon">
                {status === 'completed' && <TbCheck />}
                {status === 'in_progress' && <Loader size="small" />}
                {status === 'pending' && <TbCircle />}
              </div>
              <span className="review-progress-step__label">{step.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Render in progress state
  const renderInProgress = () => (
    <div className="export-step__section">
      {renderProgressSteps()}
    </div>
  );

  // Render completed state
  const renderCompleted = () => {
    const isFileExport = exportTarget === 'CSV' || exportTarget === 'JSON';
    const hasFailures = failedCount > 0 || uniqueErrors.length > 0;

    return (
      <div className="export-step__section export-step__section--centered">
        <div className={`export-success__icon${isPartialSuccess ? ' export-success__icon--partial' : ''}`}>
          <TbCheck size={32} />
        </div>
        <h2 className="export-success__title">{isPartialSuccess ? 'Export Partially Succeeded' : hasFailures ? 'Export Completed' : 'Export Succeeded'}</h2>
        <p className="export-success__subtitle">
          {isCrmExport
            ? `Your data has been imported to ${exportTarget === 'HUBSPOT' ? 'HubSpot' : 'Salesforce'}`
            : 'Your data has been exported and is ready to download'}
        </p>

        <div className="export-success__stats">
          <div className="export-success__stat export-success__stat--processed">
            <TbDatabase size={20} />
            <span className="export-success__stat-value">{recordCount.toLocaleString()}</span>
            <span className="export-success__stat-label">Processed</span>
          </div>
          <div
            className={`export-success__stat export-success__stat--succeeded ${isCrmExport && successCount > 0 ? 'export-success__stat--clickable' : ''} ${statusFilter === 'SUCCESS' ? 'export-success__stat--active' : ''}`}
            onClick={() => isCrmExport && successCount > 0 && handleStatClick('SUCCESS')}
          >
            <TbDatabase size={20} />
            <span className="export-success__stat-value">{successCount.toLocaleString()}</span>
            <span className="export-success__stat-label">Succeeded</span>
          </div>
          <div
            className={`export-success__stat export-success__stat--failed ${isCrmExport && failedCount > 0 ? 'export-success__stat--clickable' : ''} ${statusFilter === 'FAILED' ? 'export-success__stat--active' : ''}`}
            onClick={() => isCrmExport && failedCount > 0 && handleStatClick('FAILED')}
          >
            <TbDatabase size={20} />
            <span className="export-success__stat-value">{failedCount.toLocaleString()}</span>
            <span className="export-success__stat-label">Failed</span>
          </div>
          <div
            className={`export-success__stat export-success__stat--skipped ${isCrmExport && skippedCount > 0 ? 'export-success__stat--clickable' : ''} ${statusFilter === 'SKIPPED' ? 'export-success__stat--active' : ''}`}
            onClick={() => isCrmExport && skippedCount > 0 && handleStatClick('SKIPPED')}
          >
            <TbDatabase size={20} />
            <span className="export-success__stat-value">{skippedCount.toLocaleString()}</span>
            <span className="export-success__stat-label">Skipped</span>
          </div>
          {isFileExport && (
            <div className="export-success__stat export-success__stat--processed">
              {exportTarget === 'CSV' ? <TbFileTypeCsv size={20} /> : <TbJson size={20} />}
              <span className="export-success__stat-value">{exportTarget}</span>
              <span className="export-success__stat-label">Format</span>
            </div>
          )}
        </div>

        {isFileExport && (
          <>
            <Button
              className="export-success__download-btn green invert"
              onClick={handleDownload}
              isLoading={isDownloading}
              loadingText="Preparing download..."
            >
              <TbDownload size={18} />
              Download {exportTarget} File
            </Button>

            <p className="export-success__note">
              Your file is {exportContext?.exportData?.file_name || `export.${exportTarget?.toLowerCase()}`}
            </p>
          </>
        )}

        {isCrmExport && (
          <div className="export-success__results-container">
            {/* HubSpot List Link - show when a static list was created */}
            {exportTarget === 'HUBSPOT' &&
             exportData?.hubspot_list_id &&
             exportData?.hubspot_portal_id && (
              <div className="export-success__hubspot-list-link">
                <a
                  href={getHubspotUrl(exportData.hubspot_portal_id, 'list', exportData.hubspot_list_id) || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaExternalLinkAlt size={12} />
                  View HubSpot List
                </a>
              </div>
            )}

            {/* Error banners for failed records */}
            {uniqueErrors.length > 0 && (
              <div className="export-success__error-banners">
                {uniqueErrors.map((err, i) => (
                  <div key={i} className="export-success__error-banner">
                    <Banner color="orange">{exportTarget === 'SALESFORCE' ? `Salesforce: ${err.error}` : err.error}</Banner>
                  </div>
                ))}
              </div>
            )}

            {renderResultsTable()}
          </div>
        )}

        {renderCopySection()}
      </div>
    );
  };

  // Render failed state
  const renderFailed = () => {
    // Top-level error from the export record (skip generic "Export failed" message)
    const rawTopLevelError = exportContext?.exportData?.failure_message;
    const topLevelError = rawTopLevelError && rawTopLevelError.toLowerCase() !== 'export failed' ? rawTopLevelError : null;

    // Show loading state for CRM exports when records haven't loaded yet
    const showLoadingBars = isCrmExport && (isLoadingRecords || !exportedRecords);
    const showResultsTable = isCrmExport && !isLoadingRecords && exportedRecords && exportedRecords.length > 0;

    return (
      <div className="export-step__section export-step__section--centered">
        <div className="export-success__icon export-success__icon--failure">
          <AiFillWarning size={32} />
        </div>
        <h2 className="export-success__title export-success__title--failure">Export Failed</h2>

        {/* Show top-level error if present and not generic */}
        {topLevelError && (
          <div className="export-success__error-banners">
            <Banner color="orange">{exportTarget === 'SALESFORCE' ? `Salesforce: ${topLevelError}` : topLevelError}</Banner>
          </div>
        )}

        {/* Show per-record errors */}
        {uniqueErrors.length > 0 && (
          <div className="export-success__error-banners">
            {uniqueErrors.map((err, i) => (
              <div key={i} className="export-success__error-banner">
                <Banner color="orange">{exportTarget === 'SALESFORCE' ? `Salesforce: ${err.error}` : err.error}</Banner>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="export-success__stats">
          <div className="export-success__stat export-success__stat--processed">
            <TbDatabase size={20} />
            <span className="export-success__stat-value">{recordCount.toLocaleString()}</span>
            <span className="export-success__stat-label">Processed</span>
          </div>
          <div
            className={`export-success__stat export-success__stat--succeeded ${successCount > 0 ? 'export-success__stat--clickable' : ''} ${statusFilter === 'SUCCESS' ? 'export-success__stat--active' : ''}`}
            onClick={() => successCount > 0 && handleStatClick('SUCCESS')}
          >
            <TbDatabase size={20} />
            <span className="export-success__stat-value">{successCount.toLocaleString()}</span>
            <span className="export-success__stat-label">Succeeded</span>
          </div>
          <div
            className={`export-success__stat export-success__stat--failed ${failedCount > 0 ? 'export-success__stat--clickable' : ''} ${statusFilter === 'FAILED' ? 'export-success__stat--active' : ''}`}
            onClick={() => failedCount > 0 && handleStatClick('FAILED')}
          >
            <TbDatabase size={20} />
            <span className="export-success__stat-value">{failedCount.toLocaleString()}</span>
            <span className="export-success__stat-label">Failed</span>
          </div>
          <div
            className={`export-success__stat export-success__stat--skipped ${skippedCount > 0 ? 'export-success__stat--clickable' : ''} ${statusFilter === 'SKIPPED' ? 'export-success__stat--active' : ''}`}
            onClick={() => skippedCount > 0 && handleStatClick('SKIPPED')}
          >
            <TbDatabase size={20} />
            <span className="export-success__stat-value">{skippedCount.toLocaleString()}</span>
            <span className="export-success__stat-label">Skipped</span>
          </div>
        </div>

        {/* Show loading bars while fetching records */}
        {showLoadingBars && (
          <div className="export-success__loading-container">
            <LoadingBars />
          </div>
        )}

        {/* Show results table for CRM exports */}
        {showResultsTable && (
          <div className="export-success__results-container">
            {renderResultsTable()}
          </div>
        )}

        {renderCopySection()}
      </div>
    );
  };

  // If status doesn't match any known state, default to showing progress
  // This handles race conditions where we navigate before status updates
  const showProgress = isInProgress || (!isCompleted && !isFailed);

  // Log render decision for debugging
  console.log('[ExportStepExport] Render decision', {
    exportStatus,
    isInProgress,
    isCompleted,
    isFailed,
    showProgress,
    rendering: showProgress ? 'InProgress' : isCompleted ? 'Completed' : isFailed ? 'Failed' : 'Nothing',
    timestamp: new Date().toISOString()
  });

  return (
    <div className="content content-1200">
      <div className="export-step">
        {showProgress && renderInProgress()}
        {isCompleted && renderCompleted()}
        {isFailed && renderFailed()}
      </div>
    </div>
  );
}
